import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { type RouteComponentProps, useLocation } from 'react-router';
import AYSModal from 'shared/components/AYSModal';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import ListResModal from 'shared/components/DeleteModal';
import ExportInvoices from 'shared/components/ExportInvoices';
import { IconName } from 'shared/components/Icon';
import { Button, Dropdown } from 'shared/io';
import { useCanUseManualActions } from 'shared/utils/connector';
import { queryParamParser } from 'shared/utils/view';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Invoice } from 'types/store/invoice-state';
import { handleCreditNoteFilters } from './credit-note-filters';
import styleIdentifiers from './CreditNoteListing.module.scss';
import { type GetCreditNotesViewParams, useGetCreditNotesView } from './useGetCreditNotesView';

const styles = classNames.bind(styleIdentifiers);

type Props = RouteComponentProps;
// Builds the view-data query params from the URL: view id from the hash, filters
// from the search (parsed the same way the legacy getView saga did).
const paramsFromLocation = (hash: string, search: string): GetCreditNotesViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export default function CreditNoteListing({ history }: Props) {
  const { t } = useTranslation();
  const location = useLocation();

  const [params, setParams] = useState<GetCreditNotesViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { creditNotes, isCreditNotesInitialLoading, refetchCreditNotes } =
    useGetCreditNotesView(params);

  const [selectedCreditNotes, setSelectedCreditNotes] = useState<Invoice[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  const hasManualActionsActive = useCanUseManualActions();

  // Re-sync the query params whenever the URL changes (the orchestrator writes
  // filters/sort/pagination into the URL via history.push).
  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const newCreditNote = () => {
    creditNoteActions.detailReset();

    history.push('/credit-notes/new');
  };

  const exportInvoices = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT),
      children: <ExportInvoices resourceType="credit_note" />,
    });
  };

  const batchAction = (action_type: any, action: any) => () => {
    const data = {} as any;
    if (areAllItemsAcrossPagesSelected) {
      data.filter_params = queryParamParser(qs.parse(location.search));
      data.view_id = location.hash.slice(1);
    } else {
      data.credit_notes_ids = selectedCreditNotes.map(invoice => invoice.id);
    }
    action((values: any) => {
      creditNoteActions.batchAction({
        data: {
          ...data,
          ...values,
          action_type,
        },
        callback: (_data: any) => {
          showDialog({
            id: DialogShowId.CUSTOM,
            size: DialogShowSize.MEDIUM,
            title: t(i18nKeys.RESULT),
            children: <ListResModal data={_data} />,
          });
          refetchCreditNotes();
        },
      });
    });
  };

  const aysModal = (action_type: any) => (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CONFIRMATION),
      children: (
        <AYSModal
          text={t(
            selectedCreditNotes.length === 1 && !areAllItemsAcrossPagesSelected
              ? (i18nKeys.AYS as any)[`CN_ACTION_${action_type.toUpperCase()}`]
              : (i18nKeys.AYS as any)[`CNS_ACTION_${action_type.toUpperCase()}`],
          )}
          onConfirm={() => onSubmit({})}
        />
      ),
    });
  };

  const showCreditNote = (item: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(`/credit-notes/${item.id}`, '_blank');
      return;
    }

    history.push(`/credit-notes/${item.id}`);
  };

  return (
    <CustomTableViews
      className={styles('Listing')}
      title={t(i18nKeys.INVOICING.CREDIT_NOTE_BOOK)}
      tableName="credit_notes"
      onClickRow={showCreditNote}
      actions={
        selectedCreditNotes.length > 0 ? (
          <Dropdown
            selectorContent={
              <Button
                noMargin
                iconRight={IconName.ARROW_BOTTOM_ROUNDED}
                label={t(i18nKeys.FORM.ACTIONS)}
              />
            }
          >
            <div
              className={styles('dropdown-item')}
              onClick={batchAction('settle', aysModal('settle'))}
            >
              {t(i18nKeys.INVOICE.SETTLE)}
            </div>
            <div
              className={styles('dropdown-item')}
              onClick={batchAction('delete', aysModal('delete'))}
            >
              {t(i18nKeys.DELETE)}
            </div>
          </Dropdown>
        ) : (
          <>
            <Button
              noMargin
              label={t(i18nKeys.EXPORT)}
              onClick={exportInvoices}
              className={styles('action-button')}
            />
            {hasManualActionsActive && (
              <Button
                noMargin
                className={styles('action-button')}
                label={t(i18nKeys.INVOICING.ADD_CREDIT_NOTE)}
                onClick={newCreditNote}
              />
            )}
          </>
        )
      }
      isLoading={isCreditNotesInitialLoading}
      pagination={creditNotes?.metadata?.pagination}
      handleFilters={handleCreditNoteFilters}
      items={creditNotes?.data ?? []}
      onChangeSelectionCallback={values => setSelectedCreditNotes(values as Array<Invoice>)}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
      callbackAction={refetchCreditNotes}
    />
  );
}
